import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { photosAPI } from '../services/api';
import { piercingPhotos as mockPhotos } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Gallery = () => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [filter, setFilter] = useState('all');
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadPhotos();
  }, []);

  const loadPhotos = async () => {
    try {
      const data = await photosAPI.getAll();
      const dbPhotos = data.photos.map(p => ({
        id: p.id,
        url: `${BACKEND_URL}${p.url}`,
        title: p.title,
        category: p.category
      }));
      
      // Combine DB photos with mock photos (backwards compatible)
      setPhotos([...dbPhotos, ...mockPhotos]);
    } catch (error) {
      console.error('Error loading photos:', error);
      // Fallback to mock photos if API fails
      setPhotos(mockPhotos);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = [
    { value: 'all', label: 'All' },
    { value: 'ear', label: 'Ear' },
    { value: 'facial', label: 'Facial' },
    { value: 'oral', label: 'Oral' },
    { value: 'surface', label: 'Surface' },
    { value: 'body', label: 'Body' }
  ];

  const filteredPhotos = filter === 'all' 
    ? photos 
    : photos.filter(photo => photo.category === filter);

  return (
    <div className="min-h-screen bg-zinc-950 py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-black mb-4 text-zinc-100">GALLERY</h1>
          <p className="text-zinc-400 text-lg">Browse our professional piercing work</p>
        </div>

        {/* Filter */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setFilter(cat.value)}
              className={`px-6 py-3 font-bold tracking-wide transition-all duration-300 ${
                filter === cat.value
                  ? 'bg-red-800 text-white'
                  : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100'
              } border border-zinc-800`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        {isLoading ? (
          <div className="text-center text-zinc-400 py-12">Loading photos...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPhotos.map((photo) => (
              <div
                key={photo.id}
                onClick={() => setSelectedPhoto(photo)}
                className="relative aspect-square overflow-hidden bg-zinc-900 border border-zinc-800 group cursor-pointer hover:border-zinc-700 transition-all duration-300"
              >
                <img
                  src={photo.url}
                  alt={photo.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <div className="p-6">
                    <h3 className="text-white font-bold text-xl">{photo.title}</h3>
                    <p className="text-zinc-300 text-sm uppercase tracking-wide mt-1">{photo.category}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {selectedPhoto && (
          <div
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 text-white hover:text-zinc-300 transition-colors"
            >
              <X className="h-8 w-8" />
            </button>
            <div className="max-w-4xl w-full">
              <img
                src={selectedPhoto.url}
                alt={selectedPhoto.title}
                className="w-full h-auto max-h-[85vh] object-contain"
              />
              <div className="text-center mt-6">
                <h3 className="text-white font-bold text-2xl">{selectedPhoto.title}</h3>
                <p className="text-zinc-400 text-sm uppercase tracking-wide mt-2">{selectedPhoto.category}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;
